import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from './shell/AppShell';
import useSidebarOrder from '../hooks/useSidebarOrder';
import { exportTagsToCSV } from '../utils/exportUtils';

const COLORS = [
  { name: 'blue', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
  { name: 'green', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200', dot: 'bg-green-500' },
  { name: 'purple', bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200', dot: 'bg-purple-500' },
  { name: 'yellow', bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-200', dot: 'bg-yellow-500' },
  { name: 'pink', bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-200', dot: 'bg-pink-500' },
  { name: 'indigo', bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-200', dot: 'bg-indigo-500' },
  { name: 'orange', bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500' },
  { name: 'teal', bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-200', dot: 'bg-teal-500' },
  { name: 'red', bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
  { name: 'gray', bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200', dot: 'bg-gray-500' },
];

const getColor = (name) => COLORS.find(c => c.name === name) || COLORS[0];

const ClientTagsPage = () => {
  const navigate = useNavigate();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [colorFilter, setColorFilter] = useState(null);
  const [savedColorOrder] = useSidebarOrder('client_tags');
  const [sortBy, setSortBy] = useState('name');
  const [showModal, setShowModal] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [formData, setFormData] = useState({ name: '', color: 'blue', description: '' });
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { loadTags(); }, []);

  const loadTags = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/client/tags', { headers });
      if (res.ok) setTags(await res.json());
    } catch (err) {
      console.error('Error loading tags:', err);
    } finally {
      setLoading(false);
    }
  };

  // Color counts for sidebar
  const colorCounts = useMemo(() => {
    const counts = {};
    tags.forEach(t => { const c = t.color || 'blue'; counts[c] = (counts[c] || 0) + 1; });
    return counts;
  }, [tags]);

  const usedColors = useMemo(() => COLORS.filter(c => (colorCounts[c.name] || 0) > 0), [colorCounts]);

  const sidebarItems = useMemo(() => {
    const base = usedColors.map(c => ({
      key: c.name,
      label: null,
      title: `${c.name.charAt(0).toUpperCase() + c.name.slice(1)} (${colorCounts[c.name] || 0})`,
      icon: <span className={`w-4 h-4 rounded-full ${c.dot}`} />,
      badge: colorCounts[c.name] || 0,
      active: colorFilter === c.name,
      onClick: () => setColorFilter(colorFilter === c.name ? null : c.name),
    }));
    if (!savedColorOrder) return base;
    const ordered = [];
    savedColorOrder.forEach(key => { const item = base.find(i => i.key === key); if (item) ordered.push(item); });
    base.forEach(item => { if (!ordered.find(o => o.key === item.key)) ordered.push(item); });
    return ordered;
  }, [usedColors, colorCounts, colorFilter, savedColorOrder]);

  // Filtered + sorted
  const filtered = useMemo(() => {
    let list = tags;
    if (colorFilter) list = list.filter(t => (t.color || 'blue') === colorFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(t => (t.name || '').toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => {
      switch (sortBy) {
        case 'name': return (a.name || '').localeCompare(b.name || '');
        case 'usage': return (b.usage_count || 0) - (a.usage_count || 0);
        case 'created': return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'color': return (a.color || '').localeCompare(b.color || '');
        default: return 0;
      }
    });
  }, [tags, colorFilter, searchQuery, sortBy]);

  // Modal handlers
  const openCreate = () => { setFormData({ name: '', color: 'blue', description: '' }); setEditingTag(null); setShowModal(true); };
  const openEdit = (tag) => { setFormData({ name: tag.name, color: tag.color || 'blue', description: tag.description || '' }); setEditingTag(tag); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditingTag(null); };

  const handleSave = async () => {
    if (!formData.name.trim()) return;
    try {
      setSaving(true);
      const url = editingTag ? `/api/client/tags/${editingTag.id}` : '/api/client/tags';
      const res = await fetch(url, { method: editingTag ? 'PUT' : 'POST', headers, body: JSON.stringify(formData) });
      if (res.ok) { await loadTags(); closeModal(); }
      else { const err = await res.json(); alert(err.error || 'Failed to save tag'); }
    } catch (err) {
      console.error('Error saving tag:', err);
      alert('Failed to save tag');
    } finally { setSaving(false); }
  };

  const handleDelete = async (tag) => {
    if (!window.confirm(`Delete tag "${tag.name}"?`)) return;
    try {
      const res = await fetch(`/api/client/tags/${tag.id}`, { method: 'DELETE', headers });
      if (res.ok) setTags(prev => prev.filter(t => t.id !== tag.id));
    } catch (err) { console.error('Error deleting tag:', err); }
  };

  // ── PAGE STRIP ────────────────────────────────────────────────────────
  // Horizontal color-filter pills replace the old DraggableMiniSidebar.
  const pageStrip = (
    <div className="h-10 flex items-center px-4 gap-1.5 overflow-x-auto">
      <button
        onClick={() => setColorFilter(null)}
        className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
          !colorFilter
            ? 'bg-emerald-100 text-emerald-800'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}
      >
        All Tags
      </button>
      {sidebarItems.map((item) => (
        <button
          key={item.key}
          onClick={item.onClick}
          className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
            item.active
              ? 'bg-emerald-100 text-emerald-800'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          title={item.title}
        >
          {item.icon}
          <span className="capitalize">{item.key}</span>
          {item.badge != null && item.badge > 0 && (
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                item.active ? 'bg-emerald-200 text-emerald-900' : 'bg-white text-gray-600'
              }`}
            >
              {item.badge}
            </span>
          )}
        </button>
      ))}
      <div className="flex-1" />
      <button
        onClick={() => exportTagsToCSV(filtered)}
        className="flex-shrink-0 text-xs px-2 py-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors flex items-center gap-1.5"
        title="Export current list to CSV"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        Export
      </button>
    </div>
  );

  return (
    <AppShell variant="client" pageStrip={pageStrip}>
      <div className="h-full flex flex-col overflow-hidden bg-[color:var(--list-page-bg)]">
        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Toolbar */}
          <div className="bg-[color:var(--list-page-bg)] px-3 py-1.5 flex-shrink-0">
            <div className="flex items-center gap-2 flex-wrap">
              {colorFilter && (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] rounded font-medium border border-emerald-200">
                  <span className={`w-2 h-2 rounded-full ${getColor(colorFilter).dot}`} />
                  {colorFilter.charAt(0).toUpperCase() + colorFilter.slice(1)}
                  <button onClick={() => setColorFilter(null)} className="hover:text-emerald-900 ml-0.5">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              )}

              <div className="relative">
                <input
                  type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)}
                  placeholder="Search..."
                  className="h-[30px] w-44 pl-7 pr-2 text-sm border border-gray-300 rounded focus:outline-none transition-shadow"
                  style={searchFocused ? { boxShadow: '0 0 0 1px rgba(16, 185, 129, 0.15), 0 0 14px 2px rgba(16, 185, 129, 0.22), 0 0 28px 3px rgba(16, 185, 129, 0.10)', borderColor: 'rgba(16, 185, 129, 0.4)' } : undefined}
                />
                <svg className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>

              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                className="h-[30px] px-2 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-700 bg-white">
                <option value="name">Name A-Z</option>
                <option value="usage">Most Used</option>
                <option value="created">Newest First</option>
                <option value="color">By Color</option>
              </select>

              <div className="flex-1" />

              <span className="text-[11px] text-gray-400 tabular-nums whitespace-nowrap">
                <span className="font-medium text-gray-500">{filtered.length}</span> of <span className="font-medium text-gray-500">{tags.length}</span> tags
              </span>

              <button onClick={openCreate} className="h-[30px] px-3 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Tag
              </button>
            </div>
          </div>

          {/* Table — list-block treatment */}
          <div className="flex-1 min-h-0 mx-3 mb-1 bg-[color:var(--list-block-bg)] border rounded-lg shadow-sm overflow-hidden flex flex-col">
            <div className="flex-1 overflow-auto" style={{ scrollbarGutter: 'stable' }}>
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16">
                <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <p className="text-gray-500 text-sm">{tags.length === 0 ? 'No tags created yet' : 'No tags match your filters'}</p>
                {tags.length === 0 && (
                  <button onClick={openCreate} className="mt-4 px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">+ Create Tag</button>
                )}
              </div>
            ) : (
              <table className="w-full text-sm" style={{ minWidth: 'max-content' }}>
                <thead className="border-b sticky top-0 z-10" style={{ backgroundColor: 'var(--list-header-bg)' }}>
                  <tr>
                    <th className="px-4 py-2 text-left text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--list-header-text)' }}>Tag</th>
                    <th className="px-4 py-2 text-left text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--list-header-text)' }}>Description</th>
                    <th className="px-4 py-2 text-center text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--list-header-text)' }}>Candidates</th>
                    <th className="px-4 py-2 text-left text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--list-header-text)' }}>Created</th>
                    <th className="w-20"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(tag => {
                    const color = getColor(tag.color);
                    const count = tag.usage_count || 0;
                    return (
                      <tr key={tag.id} className="bg-white/60 hover:bg-emerald-50/50 transition-colors group">
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color.bg} ${color.text} border ${color.border}`}>
                            {tag.name}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="text-xs text-gray-500">{tag.description || '—'}</span>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          {count > 0 ? (
                            <button
                              onClick={() => navigate(`/client/archive?tag=${encodeURIComponent(tag.name)}`)}
                              className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
                            >
                              {count}
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">0</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="text-xs text-gray-500">
                            {tag.created_at ? new Date(tag.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                          </span>
                        </td>
                        <td className="px-2 py-2.5">
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => openEdit(tag)} className="p-1 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded" title="Edit">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button onClick={() => handleDelete(tag)} className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded" title="Delete">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            </div>
          </div>
        </div>
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">{editingTag ? 'Edit Tag' : 'Create Tag'}</h3>
              <button onClick={closeModal} className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Tag Name *</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., High Priority" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  autoFocus disabled={!!editingTag} />
                {editingTag && <p className="text-[10px] text-gray-400 mt-1">Tag names cannot be changed after creation</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Color</label>
                <div className="flex flex-wrap gap-2">
                  {COLORS.map((color) => (
                    <button key={color.name} onClick={() => setFormData({ ...formData, color: color.name })}
                      className={`w-8 h-8 rounded-full ${color.bg} ${color.border} border-2 flex items-center justify-center transition-all ${
                        formData.color === color.name ? 'ring-2 ring-offset-2 ring-emerald-500 scale-110' : ''
                      }`} title={color.name}>
                      {formData.color === color.name && (
                        <svg className={`w-4 h-4 ${color.text}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description (optional)</label>
                <input type="text" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="When to use this tag" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500" />
              </div>
              {formData.name.trim() && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Preview</label>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getColor(formData.color).bg} ${getColor(formData.color).text} border ${getColor(formData.color).border}`}>
                    {formData.name}
                  </span>
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button onClick={closeModal} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave} disabled={!formData.name.trim() || saving}
                className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                {saving ? 'Saving...' : (editingTag ? 'Update Tag' : 'Create Tag')}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
};

export default ClientTagsPage;
